import { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageShell } from '../components/AdminShared';
import { AddFieldDialog, FieldTable } from '../components/FieldFormShared';

const DEFAULT_FORM_TYPES = ['interpreter', 'organization', 'client'];

export default function AdminFormsPage() {
  const [formTypes, setFormTypes] = useState([]);
  const [activeType, setActiveType] = useState('interpreter');
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeLoading, setTypeLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showNewType, setShowNewType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');

  const loadForms = useCallback(async () => {
    try {
      const res = await adminAPI.listForms();
      const types = res.data.map(f => f.type);
      const all = [...new Set([...DEFAULT_FORM_TYPES, ...types])].sort();
      setFormTypes(all);
      setActiveType('interpreter');
      try { const formRes = await adminAPI.getForm('interpreter'); setTemplate(formRes.data); } catch (e) { console.error(e); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadForms(); }, [loadForms]);

  const loadForm = async (type) => {
    setActiveType(type);
    setTypeLoading(true);
    try { const res = await adminAPI.getForm(type); setTemplate(res.data); } catch (e) { console.error(e); }
    setTypeLoading(false);
  };

  const handleAdd = (field) => setTemplate(t => ({ ...t, fields: [...(t?.fields || []), field] }));
  const handleRemove = (idx) => setTemplate(t => ({ ...t, fields: t.fields.filter((_, i) => i !== idx) }));
  const handleToggleReq = (idx) => { const f = [...template.fields]; f[idx] = { ...f[idx], required: !f[idx].required }; setTemplate(t => ({ ...t, fields: f })); };

  const handleSave = async () => {
    setSaving(true);
    try { await adminAPI.updateForm(activeType, { fields: template.fields }); toast.success(`${activeType} form saved`); }
    catch (e) { console.error(e); toast.error('Save failed'); }
    setSaving(false);
  };

  const handleCreateType = async () => {
    if (!newTypeName.trim()) return;
    try {
      await adminAPI.createFormType({ type: newTypeName.trim() });
      const slug = newTypeName.trim().toLowerCase().replace(/\s/g, '_');
      setFormTypes(prev => [...prev, slug].sort());
      setShowNewType(false);
      setNewTypeName('');
      loadForm(slug);
      toast.success('Form type created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  return (
    <AdminPageShell
      loading={loading}
      title="Forms"
      subtitle="Configure profile fields for interpreters, organizations, clients, and custom forms"
      testId="admin-forms-page"
      maxWidth="4xl"
      actions={<>
        <Button variant="outline" size="sm" onClick={() => setShowNewType(true)} data-testid="add-form-type-button"><Plus className="w-4 h-4 mr-1" />New Form Type</Button>
        <Button variant="outline" size="sm" onClick={() => setShowAdd(true)} data-testid="add-form-field-button"><Plus className="w-4 h-4 mr-1" />Add Field</Button>
        <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="save-form-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Save</Button>
      </>}
    >
      {/* Form type selector */}
      <div className="flex gap-1.5 mb-6 flex-wrap">
        {formTypes.map(ft => (
          <Button key={ft} size="sm" variant={activeType === ft ? 'default' : 'outline'} onClick={() => loadForm(ft)}
            className="text-xs capitalize" data-testid={`form-type-${ft}`}>
            {ft.replace(/_/g, ' ')}
          </Button>
        ))}
      </div>

      {typeLoading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--brand))]" /></div>
      ) : (
        <FieldTable
          fields={template?.fields || []}
          onToggleRequired={handleToggleReq}
          onRemove={handleRemove}
          Table={Table} TableHeader={TableHeader} TableRow={TableRow} TableHead={TableHead}
          TableBody={TableBody} TableCell={TableCell} Badge={Badge} Button={Button}
          Trash2={Trash2} Checkbox={Checkbox}
        />
      )}

      <AddFieldDialog open={showAdd} onOpenChange={setShowAdd} onAdd={handleAdd} contextLabel={activeType} />

      {/* New Form Type Dialog */}
      <Dialog open={showNewType} onOpenChange={setShowNewType}>
        <DialogContent data-testid="new-form-type-dialog">
          <DialogHeader><DialogTitle>Create New Form Type</DialogTitle>
            <DialogDescription>Add a new profile form type</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Form Type Name *</Label><Input value={newTypeName} onChange={e => setNewTypeName(e.target.value)} data-testid="new-form-type-name" placeholder="e.g. Vendor, Partner" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewType(false)}>Cancel</Button>
            <Button onClick={handleCreateType} disabled={!newTypeName.trim()} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-new-form-type">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
